<template>
  <v-dialog :retain-focus="false" :value="sectionEditor.show" :width="1000" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>

      <v-card-text style="max-height: 70vh; overflow-y: auto">
        <div class="mb-4">
          <label class="label-input">Section Settings</label>
          <v-row>
            <v-col cols="12" md="8">
              <v-text-field v-model="fields.title" label="Section Name" hide-details />
            </v-col>
            <v-col cols="12" md="4">
              <v-checkbox v-model="fields.enableAnchor" label="Enable Anchor Link" hide-details />
            </v-col>
          </v-row>
        </div>

        <div class="mb-4">
          <SectionSettingsContainerFuild :settings="fields.settings" :container-page="fields.containerPage" />
        </div>

        <div class="mb-6">
          <label class="label-input">Card Border</label>
          <div class="group-item">
            <v-row align="center">
              <v-col cols="12" md="4">
                <v-checkbox v-model="fields.blocksBenefits.useBorder" label="Use Border" hide-details />
              </v-col>
              <v-col cols="12" md="8">
                <div class="d-flex align-center gap-3">
                  <input
                    type="color"
                    style="width: 36px; height: 36px; margin-right: 8px"
                    :value="fields.blocksBenefits.colorBorder"
                    @change="e => (fields.blocksBenefits.colorBorder = e.target.value)"
                  />
                  <v-text-field v-model="fields.blocksBenefits.colorBorder" label="Border Color" dense hide-details />
                </div>
              </v-col>
            </v-row>
          </div>
        </div>

        <div class="mb-6">
          <label class="label-input">Heading</label>
          <div class="group-item">
            <v-text-field v-model="fields.titleBenefits" label="Title Benefits" hide-details />
          </div>
        </div>

        <!-- Compare block -->
        <div class="mb-6">
          <div class="d-flex align-center justify-space-between mb-2">
            <label class="label-input mb-0">Compare Block</label>
            <v-checkbox v-model="fields.blockCompare.show" label="Show Compare Block" hide-details class="mt-0" />
          </div>

          <div v-if="fields.blockCompare.show" class="group-item">
            <v-row>
              <v-col cols="12" md="6">
                <label class="d-block mb-2 font-weight-bold">Before</label>
                <div class="d-flex align-center gap-3 mb-2">
                  <input
                    type="color"
                    style="width: 36px; height: 36px; margin-right: 8px"
                    :value="fields.blockCompare.before.backgroundColor"
                    @change="e => (fields.blockCompare.before.backgroundColor = e.target.value)"
                  />
                  <v-text-field
                    v-model="fields.blockCompare.before.backgroundColor"
                    label="Background Color"
                    dense
                    hide-details
                  />
                </div>
                <div class="d-flex align-center gap-3 mb-2">
                  <input
                    type="color"
                    style="width: 36px; height: 36px; margin-right: 8px"
                    :value="fields.blockCompare.before.textColor"
                    @change="e => (fields.blockCompare.before.textColor = e.target.value)"
                  />
                  <v-text-field v-model="fields.blockCompare.before.textColor" label="Text Color" dense hide-details />
                </div>
                <v-text-field v-model="fields.blockCompare.before.title" label="Column Title" class="mb-2" />

                <div class="d-flex justify-space-between align-center mb-2">
                  <span class="font-weight-bold">Items</span>
                  <v-btn small text color="primary" @click="handleAddItem('before')">
                    <v-icon size="14">mdi-plus</v-icon> Add Item
                  </v-btn>
                </div>
                <v-expansion-panels accordion>
                  <v-expansion-panel v-for="(item, index) in fields.blockCompare.before.items" :key="`before-${index}`">
                    <v-expansion-panel-header>
                      <div class="d-flex justify-space-between align-center">
                        <span>Item {{ index + 1 }}: {{ item.text || 'Untitled' }}</span>
                        <v-btn icon @click.stop="handleRemoveItem('before', index)">
                          <v-icon size="16" color="#ff6c6c">mdi-delete</v-icon>
                        </v-btn>
                      </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <div class="mb-3">
                        <label class="d-block mb-1">Image</label>
                        <UploadImage
                          @callback="obj => handleUploadItemImage('before', index, obj)"
                          :image="{ imageSrc: item.image || '' }"
                          :height="`80`"
                          :typeUpload="'image'"
                          @callbackDelete="() => handleRemoveItemImage('before', index)"
                        />
                      </div>
                      <v-text-field v-model="item.text" label="Item Text" hide-details />
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-col>

              <v-col cols="12" md="6">
                <label class="d-block mb-2 font-weight-bold">After</label>
                <div class="d-flex align-center gap-3 mb-2">
                  <input
                    type="color"
                    style="width: 36px; height: 36px; margin-right: 8px"
                    :value="fields.blockCompare.after.backgroundColor"
                    @change="e => (fields.blockCompare.after.backgroundColor = e.target.value)"
                  />
                  <v-text-field
                    v-model="fields.blockCompare.after.backgroundColor"
                    label="Background Color"
                    dense
                    hide-details
                  />
                </div>
                <div class="d-flex align-center gap-3 mb-2">
                  <input
                    type="color"
                    style="width: 36px; height: 36px; margin-right: 8px"
                    :value="fields.blockCompare.after.textColor"
                    @change="e => (fields.blockCompare.after.textColor = e.target.value)"
                  />
                  <v-text-field v-model="fields.blockCompare.after.textColor" label="Text Color" dense hide-details />
                </div>
                <v-text-field v-model="fields.blockCompare.after.title" label="Column Title" class="mb-2" />

                <div class="d-flex justify-space-between align-center mb-2">
                  <span class="font-weight-bold">Items</span>
                  <v-btn small text color="primary" @click="handleAddItem('after')">
                    <v-icon size="14">mdi-plus</v-icon> Add Item
                  </v-btn>
                </div>
                <v-expansion-panels accordion>
                  <v-expansion-panel v-for="(item, index) in fields.blockCompare.after.items" :key="`after-${index}`">
                    <v-expansion-panel-header>
                      <div class="d-flex justify-space-between align-center">
                        <span>Item {{ index + 1 }}: {{ item.text || 'Untitled' }}</span>
                        <v-btn icon @click.stop="handleRemoveItem('after', index)">
                          <v-icon size="16" color="#ff6c6c">mdi-delete</v-icon>
                        </v-btn>
                      </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <div class="mb-3">
                        <label class="d-block mb-1">Image</label>
                        <UploadImage
                          @callback="obj => handleUploadItemImage('after', index, obj)"
                          :image="{ imageSrc: item.image || '' }"
                          :height="`80`"
                          :typeUpload="'image'"
                          @callbackDelete="() => handleRemoveItemImage('after', index)"
                        />
                      </div>
                      <v-text-field v-model="item.text" label="Item Text" hide-details />
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-col>
            </v-row>

            <v-row class="mt-2">
              <v-col cols="12" md="6">
                <div class="d-flex align-center gap-3 mb-2">
                  <input
                    type="color"
                    style="width: 36px; height: 36px; margin-right: 8px"
                    :value="fields.blockCompare.vs.backgroundColor"
                    @change="e => (fields.blockCompare.vs.backgroundColor = e.target.value)"
                  />
                  <v-text-field
                    v-model="fields.blockCompare.vs.backgroundColor"
                    label="VS Background Color"
                    dense
                    hide-details
                  />
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="d-flex align-center gap-3 mb-2">
                  <input
                    type="color"
                    style="width: 36px; height: 36px; margin-right: 8px"
                    :value="fields.blockCompare.vs.textColor"
                    @change="e => (fields.blockCompare.vs.textColor = e.target.value)"
                  />
                  <v-text-field v-model="fields.blockCompare.vs.textColor" label="VS Text Color" dense hide-details />
                </div>
              </v-col>
            </v-row>
          </div>
        </div>

        <!-- Feature block -->
        <div class="mb-6">
          <div class="d-flex align-center justify-space-between mb-2">
            <label class="label-input mb-0">Feature Block</label>
            <v-checkbox v-model="fields.blockFeature.show" label="Show Feature Block" hide-details class="mt-0" />
          </div>

          <div v-if="fields.blockFeature.show" class="group-item">
            <div class="d-flex justify-space-between align-center mb-2">
              <span class="font-weight-bold">Items</span>
              <v-btn small text color="primary" @click="handleAddFeatureItem">
                <v-icon size="14">mdi-plus</v-icon> Add Item
              </v-btn>
            </div>
            <v-expansion-panels accordion>
              <v-expansion-panel v-for="(item, index) in fields.blockFeature.items" :key="`feature-${index}`">
                <v-expansion-panel-header>
                  <div class="d-flex justify-space-between align-center">
                    <span>Item {{ index + 1 }}</span>
                    <v-btn icon @click.stop="handleRemoveFeatureItem(index)">
                      <v-icon size="16" color="#ff6c6c">mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div class="mb-3">
                    <label class="d-block mb-1">Image</label>
                    <UploadImage
                      @callback="obj => handleUploadFeatureImage(index, obj)"
                      :image="{ imageSrc: item.image || '' }"
                      :height="`80`"
                      :typeUpload="'image'"
                      @callbackDelete="() => handleRemoveFeatureImage(index)"
                    />
                  </div>
                  <div>
                    <label class="d-block mb-1">Content</label>
                    <ckeditor :editor="editor" v-model="item.content" :config="editorConfig" />
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </div>

        <!-- Feature with index block -->
        <div class="mb-6">
          <div class="d-flex align-center justify-space-between mb-2">
            <label class="label-input mb-0">Feature With Index Block</label>
            <v-checkbox
              v-model="fields.blockFeatureWithIndex.show"
              label="Show Feature With Index Block"
              hide-details
              class="mt-0"
            />
          </div>

          <div v-if="fields.blockFeatureWithIndex.show" class="group-item">
            <v-row>
              <v-col cols="12" md="6">
                <div class="d-flex align-center gap-3">
                  <input
                    type="color"
                    style="width: 36px; height: 36px; margin-right: 8px"
                    :value="fields.blockFeatureWithIndex.backgroundColor"
                    @change="e => (fields.blockFeatureWithIndex.backgroundColor = e.target.value)"
                  />
                  <v-text-field
                    v-model="fields.blockFeatureWithIndex.backgroundColor"
                    label="Index Background Color"
                    dense
                    hide-details
                  />
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="d-flex align-center gap-3">
                  <input
                    type="color"
                    style="width: 36px; height: 36px; margin-right: 8px"
                    :value="fields.blockFeatureWithIndex.indexColor"
                    @change="e => (fields.blockFeatureWithIndex.indexColor = e.target.value)"
                  />
                  <v-text-field
                    v-model="fields.blockFeatureWithIndex.indexColor"
                    label="Index Text Color"
                    dense
                    hide-details
                  />
                </div>
              </v-col>
            </v-row>

            <div class="d-flex justify-space-between align-center mb-2 mt-2">
              <span class="font-weight-bold">Items</span>
              <v-btn small text color="primary" @click="handleAddFeatureWithIndexItem">
                <v-icon size="14">mdi-plus</v-icon> Add Item
              </v-btn>
            </div>
            <v-expansion-panels accordion>
              <v-expansion-panel
                v-for="(item, index) in fields.blockFeatureWithIndex.items"
                :key="`feature-with-index-${index}`"
              >
                <v-expansion-panel-header>
                  <div class="d-flex justify-space-between align-center">
                    <span>Item {{ index + 1 }}</span>
                    <v-btn icon @click.stop="handleRemoveFeatureWithIndexItem(index)">
                      <v-icon size="16" color="#ff6c6c">mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-text-field v-model="item.index" label="Index (e.g. 1, 2, 3)" class="mb-3" />
                  <div>
                    <label class="d-block mb-1">Content</label>
                    <ckeditor :editor="editor" v-model="item.content" :config="editorConfig" />
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </div>

        <!-- Text hint -->
        <div class="mb-4">
          <div class="d-flex align-center justify-space-between mb-2">
            <label class="label-input mb-0">Text Hint</label>
            <v-checkbox v-model="fields.textHint.show" label="Show Text Hint" hide-details class="mt-0" />
          </div>
          <div v-if="fields.textHint.show" class="group-item">
            <v-row>
              <v-col cols="12" md="6">
                <div class="d-flex align-center gap-3">
                  <input
                    type="color"
                    style="width: 36px; height: 36px; margin-right: 8px"
                    :value="fields.textHint.backgroundColor"
                    @change="e => (fields.textHint.backgroundColor = e.target.value)"
                  />
                  <v-text-field v-model="fields.textHint.backgroundColor" label="Background Color" dense hide-details />
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="d-flex align-center gap-3">
                  <input
                    type="color"
                    style="width: 36px; height: 36px; margin-right: 8px"
                    :value="fields.textHint.textColor"
                    @change="e => (fields.textHint.textColor = e.target.value)"
                  />
                  <v-text-field v-model="fields.textHint.textColor" label="Text Color" dense hide-details />
                </div>
              </v-col>
            </v-row>
            <ckeditor :editor="editor" v-model="fields.textHint.content" :config="editorConfig" />
          </div>
        </div>
      </v-card-text>

      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { updateHtmlFromData } from '../common/html/update';
import SectionSettingsContainerFuild from './common/section-settings-container-fuild.vue';

export default {
  name: 'card-cedrow-benefits-policy-section-editor',
  components: {
    UploadImage,
    SectionSettingsContainerFuild,
  },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      fields: {
        title: 'Cedrow Benefits Policy Section',
        enableAnchor: false,
        settings: {
          paddingTop: false,
          paddingBottom: false,
          backgroundColor: '#ffffff',
          textColor: '#00374c',
          overlayColor: '#000000',
          overlayOpacity: 0,
          showOverlay: false,
          backgroundImage: '',
        },
        containerPage: {
          enableStyle: false,
          background: '#FFFFFF',
        },
        blocksBenefits: {
          useBorder: true,
          colorBorder: '#123123',
        },
        titleBenefits: 'Benefis',
        blockCompare: {
          show: true,
          before: {
            backgroundColor: '#fff5f5',
            textColor: '#6b7280',
            title: 'Traditional Braces',
            items: [],
          },
          vs: {
            backgroundColor: '#00374c',
            textColor: '#ffffff',
          },
          after: {
            backgroundColor: '#f3f7fe',
            textColor: '#6b7280',
            title: 'Cedrow',
            items: [],
          },
        },
        blockFeature: {
          show: false,
          items: [],
        },
        blockFeatureWithIndex: {
          show: true,
          backgroundColor: '#eef2f3',
          indexColor: '#094a59',
          items: [],
        },
        textHint: {
          show: true,
          backgroundColor: '#f3f7fe',
          textColor: '#6b7280',
          content: '',
        },
      },
    };
  },
  mounted() {
    this.applyExtract(this.sectionEditor.sectionExtract);
  },
  watch: {
    sectionEditor: {
      handler(newVal) {
        if (newVal && newVal.sectionExtract) {
          this.applyExtract(newVal.sectionExtract);
        }
      },
      deep: true,
    },
  },
  methods: {
    applyExtract(extract) {
      if (!extract) return;
      this.fields = {
        ...this.fields,
        ...extract,
        settings: { ...this.fields.settings, ...(extract.settings || {}) },
        containerPage: { ...this.fields.containerPage, ...(extract.containerPage || {}) },
        blocksBenefits: { ...this.fields.blocksBenefits, ...(extract.blocksBenefits || {}) },
        blockCompare: {
          show: extract.blockCompare ? extract.blockCompare.show !== false : this.fields.blockCompare.show,
          before: {
            ...this.fields.blockCompare.before,
            ...((extract.blockCompare && extract.blockCompare.before) || {}),
            items:
              extract.blockCompare && Array.isArray(extract.blockCompare.before?.items)
                ? JSON.parse(JSON.stringify(extract.blockCompare.before.items))
                : this.fields.blockCompare.before.items,
          },
          vs: { ...this.fields.blockCompare.vs, ...((extract.blockCompare && extract.blockCompare.vs) || {}) },
          after: {
            ...this.fields.blockCompare.after,
            ...((extract.blockCompare && extract.blockCompare.after) || {}),
            items:
              extract.blockCompare && Array.isArray(extract.blockCompare.after?.items)
                ? JSON.parse(JSON.stringify(extract.blockCompare.after.items))
                : this.fields.blockCompare.after.items,
          },
        },
        blockFeature: {
          show: extract.blockFeature ? extract.blockFeature.show === true : this.fields.blockFeature.show,
          items:
            extract.blockFeature && Array.isArray(extract.blockFeature.items)
              ? JSON.parse(JSON.stringify(extract.blockFeature.items))
              : this.fields.blockFeature.items,
        },
        blockFeatureWithIndex: {
          ...this.fields.blockFeatureWithIndex,
          ...(extract.blockFeatureWithIndex || {}),
          items:
            extract.blockFeatureWithIndex && Array.isArray(extract.blockFeatureWithIndex.items)
              ? JSON.parse(JSON.stringify(extract.blockFeatureWithIndex.items))
              : this.fields.blockFeatureWithIndex.items,
        },
        textHint: { ...this.fields.textHint, ...(extract.textHint || {}) },
      };
    },
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const updateHtml = updateHtmlFromData.sectionCardCedrowBenefitsPolicy(
        this.sectionEditor.section.html,
        this.fields,
      );
      this.$emit('update:section', {
        ...this.sectionEditor.section,
        title: this.fields.title,
        html: updateHtml,
      });
      this.$emit('close');
    },
    handleAddItem(side) {
      const imgDefaultBefore = 'https://cdn.skyfastimg.com/images/2026/08/05/image-6a729e9c72ce8e51771d9d0a.png';
      const imgDefaultAfter = 'https://cdn.skyfastimg.com/images/2026/08/05/image-6a729fdd2d69eca21259ad27.png';
      if (side === 'before') {
        this.fields.blockCompare[side].items.push({ image: imgDefaultBefore, text: 'New item text' });
      } else {
        this.fields.blockCompare[side].items.push({ image: imgDefaultAfter, text: 'New item text' });
      }
    },
    handleRemoveItem(side, index) {
      this.fields.blockCompare[side].items.splice(index, 1);
    },
    handleUploadItemImage(side, index, obj) {
      if (!this.fields.blockCompare[side].items[index]) return;
      this.$set(this.fields.blockCompare[side].items[index], 'image', obj.imageSrc || '');
    },
    handleRemoveItemImage(side, index) {
      if (!this.fields.blockCompare[side].items[index]) return;
      this.$set(this.fields.blockCompare[side].items[index], 'image', '');
    },
    handleAddFeatureItem() {
      this.fields.blockFeature.items.push({ image: '', content: '<h3>Feature title</h3><p>Description.</p>' });
    },
    handleRemoveFeatureItem(index) {
      this.fields.blockFeature.items.splice(index, 1);
    },
    handleUploadFeatureImage(index, obj) {
      if (!this.fields.blockFeature.items[index]) return;
      this.$set(this.fields.blockFeature.items[index], 'image', obj.imageSrc || '');
    },
    handleRemoveFeatureImage(index) {
      if (!this.fields.blockFeature.items[index]) return;
      this.$set(this.fields.blockFeature.items[index], 'image', '');
    },
    handleAddFeatureWithIndexItem() {
      const newIndex = this.fields.blockFeatureWithIndex.items.length + 1;
      this.fields.blockFeatureWithIndex.items.push({
        index: String(newIndex),
        content: '<h3>Step title</h3><p>Description.</p>',
      });
    },
    handleRemoveFeatureWithIndexItem(index) {
      this.fields.blockFeatureWithIndex.items.splice(index, 1);
    },
  },
};
</script>

<style scoped lang="scss">
.group-item {
  padding: 15px 10px;
  border-radius: 10px;
  border: 1px solid #e0e0e0;
}
.label-input {
  font-size: 18px !important;
  font-weight: 500;
  color: #000;
  margin-bottom: 10px;
  display: block;
}
.header-modal {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  padding: 0 16px;
  box-shadow: 0px 0px 12px #0000000f;
}
.footer-modal {
  position: sticky;
  padding: 0 16px;
  bottom: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  box-shadow: 0px 0px 12px #0000000f;
}
</style>
